import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from './LanguageContext';
import { questions } from '../data/questions';
import './QuizScreen.css';

const QuizScreen = ({ onComplete }) => {
  const { t, language } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);

  const handleAnswer = (answerIndex) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestion].id]: answerIndex
    }));

    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      } else {
        calculateResults();
      }
      setIsAnimating(false);
    }, 500);
  };

  const calculateResults = () => {
    const scores = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
    
    questions.forEach(question => {
      const answerIndex = answers[question.id];
      if (answerIndex !== undefined) {
        const weight = question.weights[answerIndex];
        const [dim1, dim2] = question.category.split('');
        
        if (weight > 0) {
          scores[dim1] += weight;
        } else if (weight < 0) {
          scores[dim2] += Math.abs(weight);
        }
      }
    });

    const result = [
      scores.E >= scores.I ? 'E' : 'I',
      scores.S >= scores.N ? 'S' : 'N',
      scores.T >= scores.F ? 'T' : 'F',
      scores.J >= scores.P ? 'J' : 'P'
    ].join('');

    onComplete(result, scores);
  };

  const goToPrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="quiz-container">
      {/* Header */}
      <div className="quiz-header">
        <div className="quiz-title">
          {language === 'ur' ? 'MBTI شخصیت کا کوئز' : 'MBTI Personality Quiz'}
        </div>
        <div className="progress-container">
          <div className="progress-bar">
            <motion.div 
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="progress-text">
            {t('quiz.progress', { 
              current: currentQuestion + 1, 
              total: questions.length 
            })}
          </div>
        </div>
      </div>

      {/* Question Card */}
      <div className="question-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            className="question-card"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            dir={language === 'ur' ? 'rtl' : 'ltr'}
          >
            <div className="question-number">
              {language === 'ur' ? 'سوال' : 'Question'} {currentQuestion + 1}
            </div>
            
            <h2 className="question-text">
              {language === 'ur' ? currentQ.ur.question : currentQ.en.question}
            </h2>

            <div className="options-container">
              {currentQ.en.options.map((option, index) => (
                <motion.button
                  key={index}
                  className={`option-button ${answers[currentQ.id] === index ? 'selected' : ''}`}
                  onClick={() => handleAnswer(index)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isAnimating}
                >
                  <span className="option-letter">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="option-text">
                    {language === 'ur' ? currentQ.ur.options[index] : option}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="quiz-navigation">
        <button 
          className="nav-button prev-button"
          onClick={goToPrevious}
          disabled={currentQuestion === 0}
        >
          {t('quiz.previous')}
        </button>
        
        <div className="question-dots">
          {questions.map((_, index) => (
            <div
              key={index}
              className={`dot ${index === currentQuestion ? 'active' : ''} ${answers[questions[index].id] !== undefined ? 'answered' : ''}`}
            />
          ))}
        </div>
      </div>

      {/* Loading overlay */}
      {isAnimating && (
        <motion.div 
          className="loading-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="loading-spinner" />
        </motion.div>
      )}
    </div>
  );
};

export default QuizScreen;
